import React, { useMemo, useState } from 'react';
import { Ticket, TicketStatus } from '../types';
import { Card, Button, Input, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import {
	EyeOutlined,
	CalendarOutlined,
	DownOutlined,
	UpOutlined,
	ClockCircleOutlined,
	SyncOutlined,
	CheckCircleOutlined,
	PhoneOutlined,
} from '@ant-design/icons';
import './FormResponsesComponents.scss';

interface TicketCardProps {
	ticket: Ticket;
	onStatusChange: (ticketId: string, status: TicketStatus) => void;
	onNoteSave: (ticketId: string, note: string) => void;
	onViewDetails: (ticket: Ticket) => void;
	onViewCallDetails: (ticket: Ticket) => void;
}

const statusConfig: {
	[key in TicketStatus]: {
		backgroundColor: string;
		color: string;
		icon: React.ReactNode;
		buttonColor: string;
		buttonBg: string;
		label: string;
	};
} = {
	OPEN: {
		backgroundColor: '#eff6ff',
		color: '#1f2937',
		icon: <ClockCircleOutlined />,
		buttonColor: '#3b82f6',
		buttonBg: '#dbeafe',
		label: 'Open',
	},
	IN_PROGRESS: {
		backgroundColor: '#fefce8',
		color: '#1f2937',
		icon: <SyncOutlined />,
		buttonColor: '#f59e0b',
		buttonBg: '#fef3c7',
		label: 'In Progress',
	},
	COMPLETED: {
		backgroundColor: '#f0fdf4',
		color: '#6b7280',
		icon: <CheckCircleOutlined />,
		buttonColor: '#10b981',
		buttonBg: '#d1fae5',
		label: 'Completed',
	},
};

export const TicketCard: React.FC<TicketCardProps> = ({
	ticket,
	onStatusChange,
	onNoteSave,
	onViewDetails,
	onViewCallDetails,
}) => {
	const { callerPhone, callDate, responses, questions, status } = ticket;
	const [localNote, setLocalNote] = useState(ticket.note || '');
	const [isExpanded, setIsExpanded] = useState(false);

	const answersMap = useMemo(() => {
		return responses.reduce<Record<string, string>>((acc, curr) => {
			acc[curr.question] = curr.answer;
			return acc;
		}, {});
	}, [responses]);

	const handleNoteBlur = () => {
		if (localNote.trim() !== ticket.note) {
			onNoteSave(ticket.id, localNote.trim());
		}
	};

	const formatDate = (dateInMs: number) =>
		new Date(dateInMs).toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		});

	const getMenuItems = (): MenuProps['items'] => {
		const allOptions: MenuProps['items'] = [
			{ key: 'OPEN', label: 'Re-open Ticket' },
			{ key: 'IN_PROGRESS', label: 'Mark In Progress' },
			{ key: 'COMPLETED', label: 'Mark Complete' },
		];
		return allOptions.filter((item) => item && item.key !== status);
	};

	return (
		<Card
			className="response-card"
			style={{ backgroundColor: statusConfig[status].backgroundColor }}
			hoverable
			styles={{ body: { padding: '16px' } }}
		>
			<div className="response-card-header">
				<div>
					<div className="caller-info-name" style={{ color: statusConfig[status].color }}>
						{callerPhone}
					</div>
					<div className="caller-info-date">
						<CalendarOutlined />
						{formatDate(callDate)}
					</div>
					{status === 'IN_PROGRESS' && ticket.inProgressAt && (
						<div className="status-timestamp in-progress">
							<ClockCircleOutlined /> Started {formatDate(ticket.inProgressAt)}
						</div>
					)}
					{status === 'COMPLETED' && ticket.completedAt && (
						<div className="status-timestamp completed">
							Completed {formatDate(ticket.completedAt)}
						</div>
					)}
				</div>
				<div>
					<Button
						type="primary"
						size="small"
						icon={<EyeOutlined />}
						onClick={() => onViewDetails(ticket)}
						className="view-details-button"
					>
						View Details
					</Button>
				</div>
			</div>

			<div className="answers-container">
				{questions.slice(0, 2).map((question, qIdx) => (
					<div key={qIdx} className="answer-item">
						<div className="answer-question">{question}</div>
						<div className="answer-text" style={{ color: statusConfig[status].color }}>
							{answersMap[question] || '—'}
						</div>
					</div>
				))}
				{isExpanded &&
					questions.slice(2).map((question, qIdx) => (
						<div key={`expanded-${qIdx}`} className="answer-item">
							<div className="answer-question">{question}</div>
							<div className="answer-text" style={{ color: statusConfig[status].color }}>
								{answersMap[question] || '—'}
							</div>
						</div>
					))}
				{questions.length > 2 && (
					<Button
						type="link"
						size="small"
						onClick={() => setIsExpanded(!isExpanded)}
						className="show-more-button"
					>
						{isExpanded ? 'Show Less' : `Show ${questions.length - 2} More`}{' '}
						{isExpanded ? <UpOutlined /> : <DownOutlined />}
					</Button>
				)}
			</div>

			<Input.TextArea
				value={localNote}
				onChange={(e) => setLocalNote(e.target.value)}
				onBlur={handleNoteBlur}
				placeholder="Add a note..."
				autoSize={{ minRows: 3, maxRows: 5 }}
				className="note-textarea"
			/>

			<div className="response-card-footer">
				<Dropdown
					menu={{
						items: getMenuItems(),
						onClick: (e) => onStatusChange(ticket.id, e.key as TicketStatus),
					}}
					trigger={['click']}
					popupRender={(menu) => <div className="status-dropdown-wrapper">{menu}</div>}
				>
					<Button
						size="small"
						className={`status-button status-${status.toLowerCase().replace('_', '-')}`}
					>
						{statusConfig[status].icon}
						<span className="status-text">{statusConfig[status].label}</span>
						<DownOutlined className="status-dropdown-icon" />
					</Button>
				</Dropdown>

				<Button
					type="primary"
					size="small"
					icon={<PhoneOutlined />}
					onClick={() => onViewCallDetails(ticket)}
					className="call-details-button"
				>
					Call Details
				</Button>
			</div>
		</Card>
	);
};
